import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, ShieldCheck, Building2, CreditCard, Landmark, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => {
  const n = Number(v || 0);
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export default function PublicQuote() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [method, setMethod] = useState('card');
  const [accepting, setAccepting] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [verifying, setVerifying] = useState(params.get('paid') === '1');
  const [paidConfirmed, setPaidConfirmed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/crm/projections/public/quote/${token}`);
        if (!res.ok) throw new Error('not found');
        const data = await res.json();
        setQuote(data);
        if (data.accepted && data.bank) setBankInfo(data.bank);
      } catch (e) { setError('This quote link is invalid or has expired.'); }
      finally { setLoading(false); }
    })();
  }, [token]);

  // Confirm payment when returning from Stripe (?paid=1&session_id=...)
  useEffect(() => {
    if (params.get('paid') !== '1') return;
    const sid = params.get('session_id');
    (async () => {
      try {
        const res = await fetch(`${API}/api/crm/projections/public/quote/${token}/status?session_id=${encodeURIComponent(sid || '')}`);
        const d = await res.json();
        setPaidConfirmed(!!d.paid);
      } catch (e) { /* show generic success */ setPaidConfirmed(true); }
      finally { setVerifying(false); }
    })();
  }, [token, params]);

  const accept = async () => {
    if (!name.trim()) { setError('Please enter your name to accept.'); return; }
    setError(''); setAccepting(true);
    try {
      const res = await fetch(`${API}/api/crm/projections/public/quote/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepter_name: name, accepter_email: email, payment_method: method, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error('accept failed');
      const d = await res.json();
      if (d.checkout_url) { window.location.href = d.checkout_url; return; }
      if (d.bank) { setBankInfo(d.bank); return; }
      setQuote((q) => ({ ...q, accepted: true }));
    } catch (e) { setError('Could not accept the quote. Please try again or contact us.'); }
    finally { setAccepting(false); }
  };

  const copy = (txt) => { try { navigator.clipboard.writeText(String(txt)); } catch (e) {} };

  if (loading || verifying) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="public-quote-loading">
        <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6" data-testid="public-quote-error">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-slate-900">Quote unavailable</h1>
          <p className="mt-2 text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  // Friendly "you're all set" confirmation after card payment
  if (params.get('paid') === '1') {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4 py-10" data-testid="public-quote-thanks">
        <div className="mx-auto max-w-lg text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-emerald-700">
            <CheckCircle2 className="h-9 w-9" />
          </div>
          <h1 className="mt-5 text-3xl font-extrabold text-slate-900">You're all set!</h1>
          <p className="mt-3 text-slate-600">
            {paidConfirmed
              ? `Thank you${quote?.account_name ? `, ${quote.account_name}` : ''} — your Corvana subscription is active and your first payment is confirmed.`
              : 'Thanks — we\'ve received your acceptance. Your payment is being confirmed and your subscription will activate shortly.'}
          </p>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
            <div className="text-sm font-semibold text-slate-900">What happens next</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> A receipt is on its way to your email.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> Your Corvana onboarding lead will reach out to schedule kickoff.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> Rollout begins with your trial sites, then ramps to the full network.</li>
            </ul>
          </div>
          <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Payments processed securely by Stripe.
          </p>
        </div>
      </div>
    );
  }

  // Bank-transfer confirmation (chose CommBank direct deposit)
  if (bankInfo) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4 py-10" data-testid="public-quote-bank">
        <div className="mx-auto max-w-lg">
          <div className="text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-9 w-9" />
            </div>
            <h1 className="mt-5 text-3xl font-extrabold text-slate-900">Quote accepted — thank you!</h1>
            <p className="mt-3 text-slate-600">Please transfer the amount below to activate your Corvana rollout. Use the reference so we can match your payment.</p>
          </div>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="pq-bank-details">
            {[
              ['Bank', bankInfo.bank],
              ['Account name', bankInfo.account_name],
              ['BSB', bankInfo.bsb],
              ['Account number', bankInfo.account_number],
              ['Reference', bankInfo.reference],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-slate-100 py-3 last:border-0">
                <span className="text-sm text-slate-500">{k}</span>
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  {v}
                  <button onClick={() => copy(v)} className="text-slate-400 hover:text-teal-700" data-testid={`pq-copy-${k.toLowerCase().replace(/\s/g, '-')}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            ))}
            <div className="mt-4 rounded-lg bg-teal-50 border border-teal-100 p-4 text-sm">
              <div className="flex justify-between text-teal-800"><span>First payment (monthly + setup)</span>
                <span className="font-bold">{money(Number(bankInfo.amount_monthly) + Number(bankInfo.amount_setup))}</span></div>
              <div className="mt-1 text-xs text-teal-700">Then {money(bankInfo.amount_monthly)}/mo ongoing · {bankInfo.currency}</div>
            </div>
          </div>
          <p className="mt-5 text-center text-sm text-slate-500">We'll email you a confirmation once the transfer is received.</p>
        </div>
      </div>
    );
  }

  const accepted = quote.accepted;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="public-quote-page">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="rounded-t-2xl bg-teal-700 px-8 py-7 text-white">
          <div className="text-xs uppercase tracking-widest text-teal-100">Corvana · Commercial proposal</div>
          <div className="mt-1 text-3xl font-extrabold">Corvana</div>
          <div className="mt-1 text-teal-100 text-sm">Multi-site operations intelligence</div>
        </div>

        <div className="rounded-b-2xl border border-t-0 border-slate-200 bg-white px-8 py-8 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Building2 className="h-4 w-4" /> Prepared for
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="pq-account-name">{quote.account_name}</h1>
          {quote.sites ? (
            <p className="text-slate-500 text-sm mt-1">{quote.sites} sites · {quote.contract_length_months ? `${quote.contract_length_months}-month term` : 'month-to-month'}</p>
          ) : null}

          {/* Pricing */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-teal-50 border border-teal-100 p-5">
              <div className="text-xs uppercase tracking-wide text-teal-700">Recurring</div>
              <div className="text-3xl font-extrabold text-teal-800" data-testid="pq-monthly">{money(quote.monthly_total)}<span className="text-base font-semibold">/mo</span></div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
              <div className="text-xs uppercase tracking-wide text-slate-500">Setup (one-off)</div>
              <div className="text-3xl font-extrabold text-slate-900" data-testid="pq-setup">{money(quote.setup_total)}</div>
            </div>
          </div>

          <div className="mt-5 space-y-2 text-sm">
            {quote.service_monthly ? (
              <div className="flex justify-between text-slate-600"><span>Enterprise Success & Support</span><span>{money(quote.service_monthly)}/mo</span></div>
            ) : null}
            {quote.insurance_monthly ? (
              <div className="flex justify-between text-slate-600"><span>Insurance allowance (PI + cyber)</span><span>{money(quote.insurance_monthly)}/mo</span></div>
            ) : null}
            <div className="flex justify-between text-slate-600"><span>Payment terms</span><span>{quote.payment_terms || 'Net 14'} · {quote.currency}</span></div>
          </div>

          <p className="mt-4 text-xs text-slate-400">{quote.note}</p>

          {/* Accept */}
          <div className="mt-8 border-t border-slate-100 pt-6">
            {accepted ? (
              <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800" data-testid="pq-accepted">
                <CheckCircle2 className="h-6 w-6" />
                <div>
                  <div className="font-semibold">Quote accepted — thank you!</div>
                  <div className="text-sm text-emerald-700">Our team will be in touch to kick off your rollout.</div>
                </div>
              </div>
            ) : (
              <>
                <div className="text-sm font-semibold text-slate-900">Accept & set up billing</div>
                <p className="text-sm text-slate-500 mt-1">Enter your details, choose how you'd like to pay, and accept this proposal.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="pq-name" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for receipts" type="email"
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="pq-email" />
                </div>

                {/* Payment method */}
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <button onClick={() => setMethod('card')}
                    className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${method === 'card' ? 'border-teal-600 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid="pq-method-card">
                    <CreditCard className={`h-5 w-5 ${method === 'card' ? 'text-teal-700' : 'text-slate-400'}`} />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Card / subscription</div>
                      <div className="text-xs text-slate-500">Pay now, auto-renews monthly</div>
                    </div>
                  </button>
                  <button onClick={() => setMethod('bank_transfer')}
                    className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors ${method === 'bank_transfer' ? 'border-teal-600 bg-teal-50' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid="pq-method-bank">
                    <Landmark className={`h-5 w-5 ${method === 'bank_transfer' ? 'text-teal-700' : 'text-slate-400'}`} />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Bank transfer</div>
                      <div className="text-xs text-slate-500">Direct deposit (CommBank)</div>
                    </div>
                  </button>
                </div>

                {error ? <p className="mt-2 text-sm text-rose-600" data-testid="pq-error">{error}</p> : null}
                <button onClick={accept} disabled={accepting}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-teal-700 px-6 py-3 text-white font-semibold hover:bg-teal-800 disabled:opacity-60 transition-colors w-full sm:w-auto"
                  data-testid="pq-accept-btn">
                  {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  {accepting ? 'Processing…' : (method === 'card' ? 'Accept & continue to payment' : 'Accept & get bank details')}
                </button>
                <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Card payments processed securely by Stripe.</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
